import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Heart,
  HeartPulse,
  AlertTriangle,
  Shield,
  Activity,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export const SafetyFirstAidDetailContent = () => {
  const importancePoints = [
    {
      title: 'Saves Lives',
      icon: HeartPulse,
      description: 'Immediate first aid intervention during accidents, cardiac arrest, choking, or severe bleeding can significantly increase survival rates. Trained individuals can stabilize a victim before emergency services arrive.',
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      title: 'Reduces Severity of Injuries',
      icon: AlertTriangle,
      description: 'Quick and correct first aid can prevent injuries from worsening, reduce recovery time, and minimize long-term complications.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Creates a Safer Workplace',
      icon: Shield,
      description: 'Organizations that prioritize safety training experience fewer workplace accidents, reduced downtime, and lower compensation claims. A strong safety culture protects employees and improves productivity.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Legal and Compliance Requirements',
      icon: CheckCircle2,
      description: 'Many industries are legally required to provide safety and first aid training to employees. Compliance helps organizations avoid penalties and ensures readiness during audits.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Builds Confidence and Responsibility',
      icon: Activity,
      description: 'Training empowers individuals to take responsibility during emergencies, fostering confidence, leadership, and teamwork.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  const keyComponents = [
    {
      title: 'Basic First Aid Skills',
      topics: ['Cuts, wounds, and bleeding control', 'Burns and scalds', 'Fractures and sprains', 'Choking management', 'Shock treatment']
    },
    {
      title: 'CPR and AED Training',
      description: 'Cardiopulmonary Resuscitation (CPR) and Automated External Defibrillator (AED) training are crucial components. These skills are vital in cases of cardiac arrest and can dramatically improve survival chances.'
    },
    {
      title: 'Emergency Response Procedures',
      description: 'Training covers how to respond to emergencies such as fires, electrical accidents, chemical spills, and natural disasters.',
      topics: ['Fires', 'Electrical accidents', 'Chemical spills', 'Natural disasters']
    },
    {
      title: 'Workplace Safety Awareness',
      topics: ['Hazard recognition', 'Proper use of personal protective equipment (PPE)', 'Ergonomic safety', 'Fire safety and prevention']
    },
    {
      title: 'Infection Control and Hygiene',
      topics: ['Hand hygiene practices', 'Use of gloves and masks', 'Prevention of cross-contamination']
    }
  ];

  const targetAudience = [
    {
      title: 'Corporate Employees',
      description: 'From offices to factories, trained employees help maintain a safe working environment and respond effectively to emergencies.'
    },
    {
      title: 'Healthcare Professionals',
      description: 'Doctors, nurses, and support staff require advanced first aid and emergency response training to manage critical situations.'
    },
    {
      title: 'Teachers and School Staff',
      description: 'Children are vulnerable to accidents. Trained educators can handle injuries, choking incidents, and medical emergencies confidently.'
    },
    {
      title: 'Construction and Industrial Workers',
      description: 'High-risk environments demand rigorous safety and first aid training to prevent severe injuries and fatalities.'
    },
    {
      title: 'General Public and Families',
      description: 'First aid knowledge is valuable at home and in public places, enabling individuals to protect loved ones and assist others in emergencies.'
    }
  ];

  const professionalBenefits = [
    {
      title: 'Practical, Hands-On Learning',
      description: 'Professional training programs use simulations, demonstrations, and practice sessions to ensure real-world readiness.'
    },
    {
      title: 'Certified and Standardized Knowledge',
      description: 'Accredited training ensures participants receive up-to-date, internationally recognized skills aligned with safety standards.'
    },
    {
      title: 'Improved Emergency Preparedness',
      description: 'Organizations with trained personnel respond faster and more effectively during emergencies, reducing chaos and panic.'
    },
    {
      title: 'Enhanced Organizational Reputation',
      description: 'Companies that invest in employee safety are viewed as responsible and caring, improving employer branding and trust.'
    }
  ];

  const workplaceAdvantages = [
    'Fewer accidents and injuries',
    'Reduced absenteeism',
    'Lower insurance and compensation costs',
    'Improved employee morale'
  ];

  const providerCriteria = [
    'Accreditation and certification',
    'Experienced and qualified trainers',
    'Industry-specific training modules',
    'Hands-on practical sessions',
    'Post-training support and assessments'
  ];

  const futureAdvancements = [
    'Digital and blended learning models',
    'Virtual simulations and e-learning platforms',
    'Advanced life-saving techniques',
    'Industry-specific safety certifications'
  ];

  const faqs = [
    {
      question: 'What is covered in safety and first aid training?',
      answer: 'Safety and first aid training covers basic first aid skills (cuts, burns, fractures), CPR and AED training, emergency response procedures (fires, electrical accidents, chemical spills), workplace safety awareness, and infection control practices.'
    },
    {
      question: 'Who should attend safety and first aid training?',
      answer: 'Everyone benefits from safety and first aid training including corporate employees, healthcare professionals, teachers and school staff, construction and industrial workers, and the general public. It\'s especially critical in high-risk environments and workplaces.'
    },
    {
      question: 'How long does safety and first aid training take?',
      answer: 'Most safety and first aid training programs are completed in 1-2 days depending on the level and depth of training. Advanced courses or specialized industry training may require additional time.'
    },
    {
      question: 'Is certification provided after completing the training?',
      answer: 'Yes, participants receive a First Aid & Safety Certification upon successful completion of the training program. The certification is recognized and valid for employment and compliance purposes.'
    },
    {
      question: 'Why is safety training important for workplaces?',
      answer: 'Safety training reduces workplace accidents, ensures legal compliance, lowers insurance and compensation costs, improves employee morale, and creates a culture of safety. It also helps organizations respond effectively during emergencies, potentially saving lives.'
    },
    {
      question: 'What delivery modes are available for safety and first aid training?',
      answer: 'Safety and first aid training is available in classroom settings, on-site at your organization, and through hands-on practice sessions. Practical, hands-on training is essential for developing real-world emergency response skills.'
    }
  ];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="space-y-16">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      {/* What Is Safety and First Aid Training - H2 */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What Is Safety and First Aid Training?
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Safety and first aid training is a structured program designed to educate individuals on:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {[
                  'Identifying potential hazards',
                  'Preventing accidents and injuries',
                  'Responding effectively to medical emergencies',
                  'Providing immediate care to injured or ill persons'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                The training typically combines theoretical knowledge with hands-on practical skills, ensuring participants can act calmly and efficiently during real-life emergencies.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Importance Section - H2 */}
      <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Importance of Safety and First Aid Training
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {importancePoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <motion.div
                  key={point.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className={`${point.bgColor} w-14 h-14 rounded-xl flex items-center justify-center mb-4`}>
                        <Icon className={`h-7 w-7 ${point.color}`} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{point.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Components - H3 */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                Key Components of Safety and First Aid Training
              </h3>
              <div className="space-y-6">
                {keyComponents.map((component, index) => (
                  <div key={index} className="p-6 bg-gray-50 rounded-lg">
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{component.title}</h4>
                    {component.description && (
                      <p className="text-gray-700 mb-3">{component.description}</p>
                    )}
                    {component.topics && (
                      <ul className="space-y-2">
                        {component.topics.map((topic, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{topic}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Needs Training - H3 */}
      <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                Who Needs Safety and First Aid Training?
              </h3>
              <div className="space-y-4">
                {targetAudience.map((audience, index) => (
                  <div key={index} className="p-5 bg-white rounded-lg shadow-sm border-l-4 border-blue-600">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{audience.title}</h4>
                    <p className="text-gray-600">{audience.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits - H3 */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                Benefits of Professional Safety and First Aid Training
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {professionalBenefits.map((benefit, index) => (
                  <div key={index} className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h4>
                    <p className="text-gray-700">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Workplace Training - H4 */}
      <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                Safety and First Aid Training in the Workplace
              </h4>
              <p className="text-lg text-gray-700 mb-6">
                Workplace safety training is a critical investment that benefits both employees and employers.
              </p>
              <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
                <p className="font-bold text-gray-900 mb-3">Key Workplace Advantages:</p>
                <ul className="space-y-2">
                  {workplaceAdvantages.map((advantage, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{advantage}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-gray-700 mt-4">
                Employers should conduct regular training sessions, refresher courses, and mock drills to keep skills current and effective.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Choosing Provider - H4 */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                Choosing the Right Safety and First Aid Training Provider
              </h4>
              <p className="text-lg text-gray-700 mb-6">
                When selecting a training provider, consider the following:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {providerCriteria.map((criterion, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{criterion}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 mt-6">
                A reliable training provider customizes programs based on organizational needs and risk profiles.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Future - H4 */}
      <section className="py-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                Future of Safety and First Aid Training
              </h4>
              <p className="text-lg text-gray-700 mb-6">
                With evolving workplace risks and public health challenges, safety and first aid training continues to advance through:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {futureAdvancements.map((advancement, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-200">
                    <CheckCircle2 className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{advancement}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 mt-6">
                Organizations that adapt to modern training methods ensure long-term safety and preparedness.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions
              </h3>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <h4 className="text-lg font-bold text-gray-900 mb-3">
                      {faq.question}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
